import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Tag, ChevronDown, Download, RefreshCw, X,
  AlertCircle, Users, Clock, CheckCircle2, Ban,
  Droplets, Scissors, Sparkles, Palette, Package, Wrench,
  AlertTriangle, FileText, MessageSquare,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_URL}/api/cobros-proveedores`;

// ─── Helpers ─────────────────────────────────────────────────────────────
const fmtDM = (d) => {
  if (!d) return '-';
  const s = String(d).slice(0, 10);
  const [, m, dd] = s.split('-');
  return `${dd}/${m}`;
};
const fmtFecha = (d) => {
  if (!d) return '-';
  const s = String(d).slice(0, 10);
  const [y, m, dd] = s.split('-');
  return `${dd}/${m}/${y}`;
};

// Checkbox: blanco al inicio, verde al marcar (forzado para evitar
// problemas de contraste con el theme primary del sistema de finanzas).
const SoftCheckbox = ({ checked, onCheckedChange, ...rest }) => (
  <Checkbox
    checked={checked}
    onCheckedChange={onCheckedChange}
    className="h-[18px] w-[18px] rounded border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-950 shadow-none transition-colors hover:border-zinc-500 dark:hover:border-zinc-400 data-[state=checked]:!bg-emerald-600 data-[state=checked]:!border-emerald-600 data-[state=checked]:!text-white data-[state=indeterminate]:!bg-emerald-600 data-[state=indeterminate]:!border-emerald-600 data-[state=indeterminate]:!text-white"
    style={{ backgroundImage: 'none', appearance: 'none', WebkitAppearance: 'none' }}
    {...rest}
  />
);

const ServicioBadge = ({ nombre }) => {
  const n = (nombre || '').toLowerCase();
  let Icon = Package;
  let cls = 'text-zinc-500';
  if (n.includes('lavan')) { Icon = Droplets; cls = 'text-cyan-600 dark:text-cyan-400'; }
  else if (n.includes('costura')) { Icon = Scissors; cls = 'text-violet-600 dark:text-violet-400'; }
  else if (n.includes('acabado')) { Icon = Sparkles; cls = 'text-amber-600 dark:text-amber-400'; }
  else if (n.includes('estampa')) { Icon = Palette; cls = 'text-rose-600 dark:text-rose-400'; }
  else if (n.includes('arreglo')) { Icon = Wrench; cls = 'text-orange-600 dark:text-orange-400'; }
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <Icon className={`h-3.5 w-3.5 ${cls}`} />
      <span className="text-muted-foreground">{nombre}</span>
    </span>
  );
};

// ─── Sub-componentes ─────────────────────────────────────────────────────
function VencidosSinMarcarPanel({ filas }) {
  const [open, setOpen] = useState(false);
  // Agrupar por proveedor
  const grupos = useMemo(() => {
    const m = new Map();
    for (const f of filas) {
      const key = f.persona_id || `__sin_${f.persona}`;
      if (!m.has(key)) m.set(key, {
        key, persona_id: f.persona_id, persona: f.persona || 'Sin proveedor',
        lotes: [], servicios: new Set(), pzs: 0, maxDias: 0,
      });
      const g = m.get(key);
      g.lotes.push(f);
      g.pzs += f.cantidad || 0;
      if (f.servicio) g.servicios.add(f.servicio);
      if ((f.dias_desde_limite || 0) > g.maxDias) g.maxDias = f.dias_desde_limite || 0;
    }
    return Array.from(m.values()).sort((a, b) => b.maxDias - a.maxDias);
  }, [filas]);

  if (!filas.length) return null;

  return (
    <div className="rounded-lg border bg-card overflow-hidden opacity-60 hover:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-muted/30 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <div>
            <p className="text-sm font-medium">
              Vencidos sin marcar por producción · <span className="tabular-nums">{filas.length}</span> lote{filas.length !== 1 ? 's' : ''}
            </p>
            <p className="text-[11px] text-muted-foreground">
              Estos lotes vencieron pero producción aún no decidió cobrarlos. Si llevan muchos días, consulta con el supervisor.
            </p>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="border-t divide-y divide-border/50">
          {grupos.map(g => (
            <div key={g.key} className="px-4 py-2.5 flex items-center gap-3 hover:bg-muted/20">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{g.persona}</p>
                <p className="text-[11px] text-muted-foreground truncate">
                  {Array.from(g.servicios).join(', ')} · {g.lotes.length} lote{g.lotes.length !== 1 ? 's' : ''} · {g.pzs} pzs
                </p>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full tabular-nums whitespace-nowrap ${
                g.maxDias >= 15 ? 'bg-red-100 text-red-800'
                : g.maxDias >= 8 ? 'bg-orange-100 text-orange-800'
                : 'bg-amber-100 text-amber-800'
              }`}>
                {g.maxDias}d
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => toast.success('Notificación enviada al supervisor de producción · feature pendiente')}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Avisar a producción
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TabPendientes({ marcados, vencidosSinMarcar, kpis, onRegistrar, refresh }) {
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [grupoExpandido, setGrupoExpandido] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    tipo_comprobante: 'factura',
    numero_comprobante: '',
    fecha_emision_comprobante: new Date().toISOString().slice(0, 10),
    observaciones_cobro: '',
  });

  // Si se llama con un solo lote (botón individual), guardamos override
  const [overrideLotes, setOverrideLotes] = useState(null);

  // Agrupar por proveedor
  const grupos = useMemo(() => {
    const m = new Map();
    for (const f of marcados) {
      const key = f.persona_id || `__sin_${f.persona}`;
      if (!m.has(key)) m.set(key, {
        key, persona_id: f.persona_id, persona: f.persona || 'Sin proveedor',
        lotes: [], servicios: new Set(), pzs: 0, maxDias: 0,
      });
      const g = m.get(key);
      g.lotes.push(f);
      g.pzs += f.cantidad || 0;
      if (f.servicio) g.servicios.add(f.servicio);
      if ((f.dias_desde_marcaje || 0) > g.maxDias) g.maxDias = f.dias_desde_marcaje || 0;
    }
    return Array.from(m.values()).sort((a, b) => b.maxDias - a.maxDias || a.persona.localeCompare(b.persona));
  }, [marcados]);

  useEffect(() => {
    setGrupoExpandido(prev => {
      const next = { ...prev };
      for (const g of grupos) if (!(g.key in next)) next[g.key] = true;
      return next;
    });
  }, [grupos]);

  const seleccionadosArr = useMemo(
    () => marcados.filter(f => seleccionados.has(f.arreglo_id)),
    [marcados, seleccionados],
  );
  const provs = useMemo(
    () => new Set(seleccionadosArr.map(s => s.persona_id || s.persona)),
    [seleccionadosArr],
  );
  const mismoProveedor = provs.size <= 1;
  const selPzs = seleccionadosArr.reduce((a, s) => a + (s.cantidad || 0), 0);

  const toggleSel = (id) => setSeleccionados(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleGrupo = (key) => setGrupoExpandido(p => ({ ...p, [key]: !p[key] }));
  const limpiarSel = () => setSeleccionados(new Set());

  const seleccionarGrupo = (g) => {
    setSeleccionados(prev => {
      const n = new Set(prev);
      const allSel = g.lotes.every(l => n.has(l.arreglo_id));
      if (allSel) g.lotes.forEach(l => n.delete(l.arreglo_id));
      else g.lotes.forEach(l => n.add(l.arreglo_id));
      return n;
    });
  };

  // Abrir modal con todos los seleccionados (batch)
  const abrirModalBatch = () => {
    if (!seleccionadosArr.length) return;
    if (!mismoProveedor) {
      toast.error('Selecciona lotes de un solo proveedor');
      return;
    }
    setOverrideLotes(null);
    setForm(f => ({ ...f, numero_comprobante: '', observaciones_cobro: '' }));
    setModalOpen(true);
  };

  // Abrir modal con un único lote
  const abrirModalIndividual = (lote) => {
    setOverrideLotes([lote]);
    setForm(f => ({ ...f, numero_comprobante: '', observaciones_cobro: '' }));
    setModalOpen(true);
  };

  const lotesParaModal = overrideLotes || seleccionadosArr;
  const totalModal = lotesParaModal.reduce((a, l) => a + (l.cantidad || 0), 0);
  const proveedorModal = lotesParaModal[0]?.persona || '-';

  const confirmar = async () => {
    if (!form.numero_comprobante.trim()) {
      toast.error('Ingresa el número de comprobante');
      return;
    }
    if (!form.fecha_emision_comprobante) {
      toast.error('Ingresa la fecha de emisión');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/registrar`, {
        arreglo_ids: lotesParaModal.map(l => l.arreglo_id),
        tipo_comprobante: form.tipo_comprobante,
        numero_comprobante: form.numero_comprobante.trim(),
        fecha_emision_comprobante: form.fecha_emision_comprobante,
        observaciones_cobro: form.observaciones_cobro || null,
      });
      toast.success(`Cobro registrado · ${lotesParaModal.length} lote${lotesParaModal.length !== 1 ? 's' : ''} con ${form.tipo_comprobante} ${form.numero_comprobante}`);
      setSeleccionados(new Set());
      setOverrideLotes(null);
      setModalOpen(false);
      refresh();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Error al registrar cobro');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 pb-28">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/40 dark:bg-amber-950/20 px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider font-medium text-amber-700/80 dark:text-amber-300/80">Pendientes</p>
          <p className="text-2xl text-amber-700 dark:text-amber-400 mt-1 tabular-nums leading-none">
            {kpis.pendientes_pzs}
            <span className="text-sm text-muted-foreground ml-1.5">pzs</span>
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            {kpis.pendientes_lotes} lote{kpis.pendientes_lotes !== 1 ? 's' : ''} marcado{kpis.pendientes_lotes !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="rounded-lg border bg-card px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground">Proveedores</p>
          <p className="text-2xl mt-1 tabular-nums leading-none">{kpis.proveedores}</p>
          <p className="text-[11px] text-muted-foreground mt-1">con cobros pendientes</p>
        </div>
        <div className={`rounded-lg border px-4 py-3 ${kpis.antiguedad_max > 7 ? 'border-red-200 dark:border-red-900/60 bg-red-50/40 dark:bg-red-950/20' : 'bg-card'}`}>
          <p className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground">Antigüedad máx</p>
          <p className={`text-2xl mt-1 tabular-nums leading-none ${kpis.antiguedad_max > 7 ? 'text-red-700 dark:text-red-400' : ''}`}>
            {kpis.antiguedad_max}
            <span className="text-sm text-muted-foreground ml-1.5">días</span>
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">desde marcaje</p>
        </div>
        <div className="rounded-lg border bg-card px-4 py-3">
          <p className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground">Cobrados este mes</p>
          <p className="text-2xl text-emerald-700 dark:text-emerald-400 mt-1 tabular-nums leading-none">
            {kpis.cobrados_mes_pzs}
            <span className="text-sm text-muted-foreground ml-1.5">pzs</span>
          </p>
          <p className="text-[11px] text-muted-foreground mt-1">
            {kpis.cobrados_mes_lotes} lote{kpis.cobrados_mes_lotes !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Lista de pendientes */}
      {grupos.length === 0 ? (
        <div className="rounded-lg border bg-card py-16 text-center">
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500/60 mb-2" />
          <p className="text-sm font-medium">Sin lotes pendientes de cobro</p>
          <p className="text-xs text-muted-foreground mt-1">Cuando producción marque lotes para cobrar aparecerán aquí.</p>
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="grid grid-cols-[36px_18px_minmax(100px,0.8fr)_minmax(150px,1.6fr)_70px_minmax(110px,1fr)_minmax(180px,1.5fr)_120px] gap-3 items-center px-3 py-2 border-b bg-muted/30 text-[10px] uppercase tracking-wider font-medium text-muted-foreground">
            <span></span><span></span>
            <span>N° corte</span>
            <span>Modelo</span>
            <span className="text-right">Cant.</span>
            <span>Servicio</span>
            <span>Marcado</span>
            <span className="text-right">Acción</span>
          </div>

          {grupos.map((g, gi) => {
            const allSel = g.lotes.every(l => seleccionados.has(l.arreglo_id));
            const someSel = g.lotes.some(l => seleccionados.has(l.arreglo_id));
            const expanded = grupoExpandido[g.key] !== false;
            const servicios = Array.from(g.servicios).slice(0, 3);
            const critico = g.maxDias >= 7;

            return (
              <div key={g.key} className={gi > 0 ? 'border-t' : ''}>
                <div
                  className="grid grid-cols-[36px_18px_1fr_auto] gap-3 items-center px-3 py-2 cursor-pointer select-none bg-zinc-100 dark:bg-zinc-800/60 hover:bg-zinc-150 dark:hover:bg-zinc-800 transition-colors"
                  onClick={() => toggleGrupo(g.key)}
                >
                  <button
                    type="button"
                    className="h-7 w-7 rounded-md hover:bg-muted/60 flex items-center justify-center text-muted-foreground"
                    onClick={(e) => { e.stopPropagation(); toggleGrupo(g.key); }}
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? '' : '-rotate-90'}`} />
                  </button>
                  <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                    <SoftCheckbox
                      checked={allSel ? true : (someSel ? 'indeterminate' : false)}
                      onCheckedChange={() => seleccionarGrupo(g)}
                      aria-label={`Seleccionar todos los lotes de ${g.persona}`}
                    />
                  </div>
                  <div className="flex items-baseline gap-2 min-w-0 flex-wrap">
                    <span className="font-medium text-sm truncate">{g.persona}</span>
                    {servicios.length > 0 && (
                      <span className="text-[11px] text-muted-foreground truncate">· {servicios.join(', ')}</span>
                    )}
                    <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
                      · {g.lotes.length} lote{g.lotes.length !== 1 ? 's' : ''} · {g.pzs} pzs
                    </span>
                  </div>
                  <div className="flex justify-end items-center gap-2">
                    {critico && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-700 inline-flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        CRÍTICO {g.maxDias}d
                      </span>
                    )}
                  </div>
                </div>

                {expanded && (
                  <div className="border-t divide-y divide-border/50 bg-amber-50/30 dark:bg-amber-950/10">
                    {g.lotes.map(l => {
                      const sel = seleccionados.has(l.arreglo_id);
                      return (
                        <div
                          key={l.arreglo_id}
                          onClick={() => toggleSel(l.arreglo_id)}
                          className={`relative grid grid-cols-[36px_18px_minmax(100px,0.8fr)_minmax(150px,1.6fr)_70px_minmax(110px,1fr)_minmax(180px,1.5fr)_120px] gap-3 items-center px-3 py-2 cursor-pointer transition-colors ${sel ? 'bg-blue-50/70 dark:bg-blue-950/30' : 'hover:bg-amber-50/50 dark:hover:bg-amber-950/20'}`}
                        >
                          {sel && <span className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500" />}
                          <span></span>
                          <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                            <SoftCheckbox
                              checked={sel}
                              onCheckedChange={() => toggleSel(l.arreglo_id)}
                              aria-label={`Seleccionar ${l.n_corte}`}
                            />
                          </div>
                          <span className="font-mono text-sm tabular-nums truncate">{l.n_corte}</span>
                          <span className="text-sm truncate" title={l.modelo || ''}>
                            {l.modelo || l.linea_negocio || '—'}
                          </span>
                          <div className="text-right tabular-nums">
                            <span className="text-sm">{l.cantidad}</span>
                            <span className="text-[10px] text-muted-foreground ml-1">pzs</span>
                          </div>
                          <ServicioBadge nombre={l.servicio} />
                          <div className="min-w-0 text-[11px] text-muted-foreground truncate">
                            <span className="font-medium text-foreground/80">{l.marcado_por_nombre || '—'}</span>
                            <span> · {fmtDM(l.fecha_marcado)}</span>
                            <span> · {l.dias_desde_marcaje}d</span>
                            {l.motivo_marcado && <span className="italic"> · {l.motivo_marcado}</span>}
                          </div>
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              className="h-7 text-xs gap-1 bg-emerald-600 text-white hover:bg-emerald-700"
                              onClick={(e) => { e.stopPropagation(); abrirModalIndividual(l); }}
                            >
                              <Tag className="h-3 w-3" />
                              Registrar cobro
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Vencidos sin marcar (sección colapsable informativa) */}
      <VencidosSinMarcarPanel filas={vencidosSinMarcar} />

      {/* Barra inferior sticky */}
      {seleccionadosArr.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/95 backdrop-blur-sm px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={limpiarSel}
                className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
                title="Limpiar selección"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="text-sm">
                <p className="font-semibold leading-tight">
                  📌 {seleccionadosArr.length} lote{seleccionadosArr.length !== 1 ? 's' : ''} · {selPzs} pzs
                </p>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  {mismoProveedor
                    ? `${seleccionadosArr[0]?.persona || '-'}`
                    : <span className="text-amber-600 dark:text-amber-400 font-semibold inline-flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Mezcla de proveedores
                      </span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={limpiarSel}>Cancelar</Button>
              <Button
                disabled={!mismoProveedor}
                onClick={abrirModalBatch}
                className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-600/40"
              >
                <Tag className="h-4 w-4" />
                Registrar cobro
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal registrar cobro */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Registrar cobro · {proveedorModal}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md border bg-muted/20 p-3 space-y-1.5 text-xs">
              <p className="text-muted-foreground mb-1">Lotes:</p>
              <ul className="space-y-0.5 max-h-24 overflow-y-auto">
                {lotesParaModal.map(l => (
                  <li key={l.arreglo_id} className="flex justify-between gap-2">
                    <span className="font-mono">{l.n_corte}</span>
                    <span className="text-muted-foreground flex-1 truncate">{l.modelo || '—'}</span>
                    <span className="tabular-nums">{l.cantidad} pzs</span>
                  </li>
                ))}
              </ul>
              <div className="pt-2 border-t flex justify-between">
                <span className="text-muted-foreground">Total prendas</span>
                <span className="text-base tabular-nums">{totalModal} <span className="text-xs text-muted-foreground">pzs</span></span>
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium">Tipo de comprobante</Label>
              <div className="mt-1 grid grid-cols-3 gap-1">
                {[
                  { v: 'factura', l: 'Factura' },
                  { v: 'boleta', l: 'Boleta' },
                  { v: 'nota_venta', l: 'Nota de Venta' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    type="button"
                    className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                      form.tipo_comprobante === opt.v
                        ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                        : 'bg-card border-zinc-300 dark:border-zinc-700 hover:bg-muted text-foreground'
                    }`}
                    onClick={() => setForm(f => ({ ...f, tipo_comprobante: opt.v }))}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium">N° de comprobante <span className="text-red-600">*</span></Label>
              <Input
                className="mt-1"
                placeholder="ej: F001-12345"
                value={form.numero_comprobante}
                onChange={(e) => setForm(f => ({ ...f, numero_comprobante: e.target.value }))}
              />
              <p className="text-[10px] text-muted-foreground mt-1">Número que emitiste en Odoo</p>
            </div>

            <div>
              <Label className="text-xs font-medium">Fecha de emisión <span className="text-red-600">*</span></Label>
              <Input
                className="mt-1"
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={form.fecha_emision_comprobante}
                onChange={(e) => setForm(f => ({ ...f, fecha_emision_comprobante: e.target.value }))}
              />
            </div>

            <div>
              <Label className="text-xs font-medium">Observaciones <span className="text-muted-foreground font-normal">(opcional)</span></Label>
              <textarea
                className="mt-1 w-full min-h-[60px] rounded-md border bg-card px-3 py-2 text-sm"
                maxLength={500}
                placeholder="Notas internas"
                value={form.observaciones_cobro}
                onChange={(e) => setForm(f => ({ ...f, observaciones_cobro: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button
              onClick={confirmar}
              disabled={submitting}
              className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-600/40"
            >
              <FileText className="h-4 w-4" />
              {submitting ? 'Registrando...' : 'Confirmar cobro'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TabCobrados({ filas, onRevertir, refresh }) {
  const [filtros, setFiltros] = useState({ proveedor: '', tipo: '' });
  const [revDialog, setRevDialog] = useState(null);
  const [motivo, setMotivo] = useState('');

  const visibles = useMemo(
    () => filas.filter(f =>
      (!filtros.proveedor || (f.persona || '').toLowerCase().includes(filtros.proveedor.toLowerCase())) &&
      (!filtros.tipo || f.tipo_comprobante === filtros.tipo)
    ),
    [filas, filtros],
  );

  // Agrupar por mes/proveedor para mejor lectura
  const grupos = useMemo(() => {
    const m = new Map();
    for (const f of visibles) {
      const ym = (f.fecha_cobro || '').slice(0, 7) || '—';
      const persona = f.persona || 'Sin proveedor';
      const key = `${ym}__${persona}`;
      if (!m.has(key)) m.set(key, { mes: ym, persona, lotes: [], pzs: 0 });
      const g = m.get(key);
      g.lotes.push(f);
      g.pzs += f.cantidad || 0;
    }
    return Array.from(m.values()).sort((a, b) => b.mes.localeCompare(a.mes) || a.persona.localeCompare(b.persona));
  }, [visibles]);

  if (!filas.length) {
    return (
      <div className="rounded-lg border bg-card py-16 text-center">
        <CheckCircle2 className="h-12 w-12 mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-sm font-medium">Sin cobros registrados</p>
        <p className="text-xs text-muted-foreground mt-1">Los cobros que registres aparecerán aquí.</p>
      </div>
    );
  }

  const confirmarRevertir = async () => {
    if (!motivo.trim()) {
      toast.error('El motivo es obligatorio');
      return;
    }
    try {
      await axios.post(`${API}/${revDialog.arreglo_id}/revertir`, { motivo });
      toast.success(`Cobro revertido · ${revDialog.n_corte}`);
      setRevDialog(null);
      setMotivo('');
      refresh();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Error al revertir');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="max-w-xs h-9"
          placeholder="Filtrar proveedor"
          value={filtros.proveedor}
          onChange={(e) => setFiltros(f => ({ ...f, proveedor: e.target.value }))}
        />
        <select
          className="h-9 rounded-md border bg-card px-3 text-sm"
          value={filtros.tipo}
          onChange={(e) => setFiltros(f => ({ ...f, tipo: e.target.value }))}
        >
          <option value="">Todos los tipos</option>
          <option value="factura">Factura</option>
          <option value="boleta">Boleta</option>
          <option value="nota_venta">Nota de Venta</option>
          <option value="nota_descuento">Nota de descuento</option>
          <option value="otro">Otro</option>
        </select>
      </div>

      <div className="space-y-3">
        {grupos.map(g => (
          <div key={`${g.mes}__${g.persona}`} className="rounded-lg border bg-card overflow-hidden">
            <div className="px-3 py-2 bg-zinc-100 dark:bg-zinc-800/60 flex items-center justify-between">
              <div className="flex items-baseline gap-2">
                <span className="font-medium text-sm">{g.persona}</span>
                <span className="text-[11px] text-muted-foreground">· {g.mes}</span>
              </div>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {g.lotes.length} lote{g.lotes.length !== 1 ? 's' : ''} · {g.pzs} pzs
              </span>
            </div>
            <div className="divide-y divide-border/50">
              {g.lotes.map(l => (
                <div key={l.arreglo_id} className="grid grid-cols-[minmax(80px,0.7fr)_minmax(140px,1.4fr)_60px_minmax(120px,1fr)_minmax(160px,1.3fr)_minmax(140px,1fr)_44px] gap-3 items-center px-3 py-2 text-sm hover:bg-muted/20">
                  <span className="font-mono tabular-nums truncate">{l.n_corte}</span>
                  <span className="truncate">{l.modelo || '—'}</span>
                  <span className="text-right tabular-nums">{l.cantidad} <span className="text-[10px] text-muted-foreground">pzs</span></span>
                  <ServicioBadge nombre={l.servicio} />
                  <span className="truncate text-xs">
                    <span className="text-muted-foreground">{l.tipo_comprobante || '—'}</span>
                    {l.numero_comprobante && <span className="ml-1 font-mono">{l.numero_comprobante}</span>}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {l.cobrado_por_nombre || '—'} · {fmtDM(l.fecha_cobro)}
                  </span>
                  <button
                    type="button"
                    onClick={() => { setRevDialog(l); setMotivo(''); }}
                    className="h-7 w-7 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 transition-colors flex items-center justify-center"
                    title="Revertir cobro"
                  >
                    <Ban className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!revDialog} onOpenChange={(v) => !v && setRevDialog(null)}>
        <DialogContent className="max-w-sm">
          {revDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Ban className="h-5 w-5 text-red-600" />
                  Revertir cobro
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <p className="text-xs text-muted-foreground">
                  Lote <span className="font-mono">{revDialog.n_corte}</span> · {revDialog.persona} · {revDialog.cantidad} pzs.
                  Volverá al estado "Marcado" en producción.
                </p>
                <div>
                  <Label className="text-xs font-medium">Motivo <span className="text-red-600">*</span></Label>
                  <Input
                    className="mt-1"
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    placeholder="ej: comprobante anulado"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRevDialog(null)}>Cancelar</Button>
                <Button variant="destructive" onClick={confirmarRevertir} className="gap-2">
                  <Ban className="h-4 w-4" />
                  Revertir
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Página principal ────────────────────────────────────────────────────
const CobrosProveedores = () => {
  const [tab, setTab] = useState('pendientes');
  const [marcados, setMarcados] = useState([]);
  const [cobrados, setCobrados] = useState([]);
  const [vencidosSinMarcar, setVencidosSinMarcar] = useState([]);
  const [kpis, setKpis] = useState({
    pendientes_pzs: 0, pendientes_lotes: 0, proveedores: 0,
    antiguedad_max: 0, cobrados_mes_pzs: 0, cobrados_mes_lotes: 0,
    vencidos_sin_marcar: 0,
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.allSettled([
        axios.get(`${API}/pendientes?estado=marcados`),
        axios.get(`${API}/pendientes?estado=cobrados`),
        axios.get(`${API}/pendientes?estado=sin_marcar_vencidos`),
        axios.get(`${API}/kpis`),
      ]);
      if (r1.status === 'fulfilled') setMarcados(r1.value.data.filas || []);
      if (r2.status === 'fulfilled') setCobrados(r2.value.data.filas || []);
      if (r3.status === 'fulfilled') setVencidosSinMarcar(r3.value.data.filas || []);
      if (r4.status === 'fulfilled') setKpis(r4.value.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleExport = async () => {
    try {
      const estado = tab === 'pendientes' ? 'marcados' : 'cobrados';
      const res = await axios.get(`${API}/export?estado=${estado}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cobros_proveedores_${estado}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo exportar');
    }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div>
        <nav className="text-xs text-muted-foreground mb-1">Cuentas por Pagar</nav>
        <h1 className="text-2xl font-semibold tracking-tight">Cobros a Proveedores</h1>
        <p className="text-sm text-muted-foreground">
          Lotes marcados por producción · pendientes de emitir comprobante
        </p>
      </div>

      {/* Tabs + Excel */}
      <div className="flex items-center gap-1 border-b">
        <button
          type="button"
          onClick={() => setTab('pendientes')}
          className={`relative px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${
            tab === 'pendientes' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Clock className="h-4 w-4" />
          Pendientes de cobro
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground tabular-nums">
            {kpis.pendientes_lotes}
          </span>
          {tab === 'pendientes' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
        </button>
        <button
          type="button"
          onClick={() => setTab('cobrados')}
          className={`relative px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2 ${
            tab === 'cobrados' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <CheckCircle2 className="h-4 w-4" />
          Cobrados
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground tabular-nums">
            {cobrados.length}
          </span>
          {tab === 'cobrados' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
        </button>
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={handleExport} className="h-8 gap-1.5" title="Exportar Excel">
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Excel</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={refresh} className="h-8 w-8 p-0" title="Refrescar">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {tab === 'pendientes' && (
        <TabPendientes
          marcados={marcados}
          vencidosSinMarcar={vencidosSinMarcar}
          kpis={kpis}
          refresh={refresh}
        />
      )}
      {tab === 'cobrados' && (
        <TabCobrados filas={cobrados} refresh={refresh} />
      )}
    </div>
  );
};

export default CobrosProveedores;
